import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { Maybe } from 'graphql/jsutils/Maybe'
import { DateTime } from 'luxon'
import { Case, Else, If, Switch, Then, When } from 'react-if'
import { GuildScore } from '@/atoms/GuildScore'
import { PlaySquareIcon } from '@/atoms/Icons/PlaySquareIcon'
import { ClosedCaptionsBadge, ContentRatingBadge, HighDefinitionBadge } from '@/atoms/MetadataBadge'
import { CaptionMD, CaptionSM } from '@/atoms/Text'
import { projectTypes } from '@/constants/projectTypes'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { ProjectTheme } from '@/types/codegen-contentful'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface HeroProjectDescriptionProps {
  showDescription?: string
  showTitle: Maybe<string>
  showEarlyAccess: boolean | undefined
  slug: string
  contentfulProjectTheme: Maybe<ProjectTheme>
  guildScore?: number
}

const useTruncated = () => {
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      const element = document.getElementById('show-description')

      if (element) {
        setIsTruncated(element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth)
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return isTruncated
}

export const HeroProjectDescription: React.FC<HeroProjectDescriptionProps> = ({
  showDescription,
  slug,
  guildScore,
}) => {
  const { t } = useTranslate('watch')
  const [expanded, setExpanded] = useState(false)
  const isTruncated = useTruncated()
  const { metadata, projectType, releaseDate, streamingAtLabel, contentRating } = useProjectMeta()
  const releaseYear = releaseDate ? DateTime.fromISO(releaseDate).year : null

  const handleReadMoreOrLess = () => {
    setExpanded(!expanded)
  }

  return (
    <>
      {streamingAtLabel && streamingAtLabel.length > 0 && (
        <div className="mb-2 flex flex-row items-center justify-center gap-2 md:justify-start lg:mb-4">
          <PlaySquareIcon size={24} color="info-200" />
          <CaptionMD className="text-left" weight="medium" color="info-200">
            {streamingAtLabel}
          </CaptionMD>
        </div>
      )}
      <div className="mb-6 flex flex-col flex-wrap items-center justify-start gap-2 !text-core-gray-400 md:items-start">
        <div className="flex flex-row flex-wrap items-center justify-center gap-1 md:justify-start lg:gap-2">
          {releaseYear && (
            <>
              <CaptionSM className="sm:photon-caption-md">{releaseYear}</CaptionSM>
              <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
            </>
          )}
          <CaptionSM className="sm:photon-caption-md whitespace-nowrap">
            <Switch>
              <Case condition={projectType === projectTypes.series}>
                <Translate i18nKey="tvSeries" t={t}>
                  TV Series
                </Translate>
              </Case>
              <Case condition={projectType === projectTypes.movie}>
                <Translate i18nKey="movie" t={t}>
                  Movie
                </Translate>
              </Case>
              <Case condition={projectType === projectTypes.podcast}>
                <Translate i18nKey="podcast" t={t}>
                  Podcast
                </Translate>
              </Case>
              <Case condition={projectType === projectTypes.special}>
                <Translate i18nKey="special" t={t}>
                  Special
                </Translate>
              </Case>
            </Switch>
          </CaptionSM>
          {metadata?.genres?.length && metadata.genres.length > 0 && (
            <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
          )}
          {metadata?.genres?.map((genre: string, index) => {
            return (
              <div className="flex gap-1 lg:gap-2" key={genre}>
                <CaptionSM className="sm:photon-caption-md">{genre}</CaptionSM>
                <When condition={index < metadata.genres.length - 1}>
                  <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                </When>
              </div>
            )
          })}
        </div>
        <div className="flex flex-row flex-wrap items-center justify-start gap-2">
          <ContentRatingBadge
            contentRating={contentRating}
            className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
          />
          <HighDefinitionBadge
            slug={slug}
            className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
          />
          <ClosedCaptionsBadge className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl" />
          <When condition={guildScore}>
            <GuildScore guildScore={guildScore} />
          </When>
        </div>
        <div className="mb-2 hidden max-w-[500px] flex-col items-center gap-2 md:flex md:items-start">
          <CaptionMD
            id="show-description"
            className={classNames('text-center md:text-left transition-all duration-600 ease-in-out', {
              'max-h-[60px] line-clamp-2': !expanded,
              'overflow-hidden max-h-96': expanded,
            })}
            weight="medium"
            color="core-gray-400"
          >
            {showDescription}
          </CaptionMD>
          <When condition={isTruncated}>
            <button onClick={handleReadMoreOrLess} className="inline-block">
              <CaptionMD
                className="cursor-pointer transition-all duration-300 ease-in-out hover:brightness-110"
                color="oxide-bright"
                weight="medium"
              >
                <If condition={expanded}>
                  <Then>
                    <Translate i18nKey="readLessv3" t={t}>
                      Read less
                    </Translate>
                  </Then>
                  <Else>
                    <Translate i18nKey="readMorev3" t={t}>
                      Read more
                    </Translate>
                  </Else>
                </If>
              </CaptionMD>
            </button>
          </When>
        </div>
      </div>
    </>
  )
}
